import clsx from "clsx";
import { FC, useState } from "react";

import {
  IProductionFiltersMetadata,
  IProductionStateLogData,
  TSystemParamsDisplayWithCategories,
} from "@ryde/shared";

import { ProductionLogStatsTab } from "./production-logs.models";
import { ProductionLogsStatsTabsInfo } from "./production-logs.stats.tabs.info";
import { ProductionLogsStatsTabsMetrics } from "./production-logs.stats.tabs.metrics";
import { ProductionLogsStatsTabsSystemParams } from "./production-logs.stats.tabs.system-params";

interface IProductionLogsStatsTabs {
  state?: IProductionStateLogData;
  productionFilters?: IProductionFiltersMetadata;
  timeInfoText?: string;
  timeInfoClassName?: string;
  duration?: string;
  hasValidTimes?: boolean;
  formattedStartedAt?: string;
  formattedProducedRideFirstDate?: string;
  formattedProducedRideEndDate?: string;
  hasProductionData?: boolean;
  currentPage?: number;
  totalPages?: number;
  filteredLogsCount?: number;
  fetchLogs?: () => void;
  handleClearProductionCacheClick?: () => void;
  producedRideCount?: number;
  joinedRideCount?: number;
  hasReadSystemParamsPermission?: boolean;
  systemParams?: TSystemParamsDisplayWithCategories;
}

export const ProductionLogsStatsTabs: FC<IProductionLogsStatsTabs> = ({
  state,
  productionFilters,
  timeInfoText,
  timeInfoClassName,
  duration,
  hasValidTimes,
  formattedStartedAt,
  formattedProducedRideFirstDate,
  formattedProducedRideEndDate,
  hasProductionData,
  currentPage,
  totalPages,
  filteredLogsCount,
  fetchLogs,
  handleClearProductionCacheClick,
  producedRideCount,
  joinedRideCount,
  hasReadSystemParamsPermission,
  systemParams,
}) => {
  const [activeTab, setActiveTab] = useState<ProductionLogStatsTab>(
    ProductionLogStatsTab.Produce,
  );

  const hasMetrics = state?.metrics && Object.keys(state.metrics).length > 0;
  const hasBookingStats =
    state?.totalBookingCount ||
    state?.producedRideCount ||
    state?.joinedRideCount;

  const shouldShowMetricsTab = Boolean(hasMetrics || hasBookingStats);
  const hasSystemParams = systemParams && Object.keys(systemParams).length > 0;
  const shouldShowSystemParamsTab =
    Boolean(hasReadSystemParamsPermission) && hasSystemParams;

  return (
    <div className="log__stats-tabs">
      <div className="log__stats-tabs-header">
        <button
          type="button"
          className={clsx("log__stats-tab", {
            active: activeTab === ProductionLogStatsTab.Produce,
          })}
          onClick={() => setActiveTab(ProductionLogStatsTab.Produce)}
        >
          Produce info
        </button>
        {shouldShowMetricsTab && (
          <button
            type="button"
            className={clsx("log__stats-tab", {
              active: activeTab === ProductionLogStatsTab.Metrics,
            })}
            onClick={() => setActiveTab(ProductionLogStatsTab.Metrics)}
          >
            Metrics
          </button>
        )}
        {shouldShowSystemParamsTab && (
          <button
            type="button"
            className={clsx("log__stats-tab", {
              active: activeTab === ProductionLogStatsTab.SystemParams,
            })}
            onClick={() => setActiveTab(ProductionLogStatsTab.SystemParams)}
          >
            System params applied
          </button>
        )}
      </div>

      <div className="log__stats-tabs-content">
        {activeTab === ProductionLogStatsTab.Produce && (
          <ProductionLogsStatsTabsInfo
            state={state}
            timeInfoText={timeInfoText}
            timeInfoClassName={timeInfoClassName}
            duration={duration}
            hasValidTimes={hasValidTimes}
            formattedStartedAt={formattedStartedAt}
            formattedProducedRideFirstDate={formattedProducedRideFirstDate}
            formattedProducedRideEndDate={formattedProducedRideEndDate}
            hasProductionData={hasProductionData}
            currentPage={currentPage}
            totalPages={totalPages}
            filteredLogsCount={filteredLogsCount}
            fetchLogs={fetchLogs}
            handleClearProductionCacheClick={handleClearProductionCacheClick}
            producedRideCount={producedRideCount}
            joinedRideCount={joinedRideCount}
          />
        )}

        {activeTab === ProductionLogStatsTab.Metrics &&
          shouldShowMetricsTab && (
            <ProductionLogsStatsTabsMetrics
              state={state}
              productionFilters={productionFilters}
            />
          )}
        {activeTab === ProductionLogStatsTab.SystemParams &&
          shouldShowSystemParamsTab && (
            <ProductionLogsStatsTabsSystemParams systemParams={systemParams} />
          )}
      </div>
    </div>
  );
};
